import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Clock, Video, FileText } from "lucide-react";
import stats from "./content/site-stats.json";
import { useMeta } from "../hooks/useMeta";
import { draftPath } from "./DraftLayout";
import { Shell } from "./parts";

const API = process.env.REACT_APP_BACKEND_URL;
const CALENDLY = stats.links.calendly;

function CalendlyInline({ name, email, appId, onBooked }) {
  useEffect(() => {
    if (!document.querySelector('script[src*="calendly.com/assets/external/widget.js"]')) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
    const onMsg = (e) => {
      if (e.origin !== "https://calendly.com" || !e.data || e.data.event !== "calendly.event_scheduled") return;
      onBooked(e.data.payload || {});
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onBooked]);
  const params = Object.entries({ hide_gdpr_banner: "1", primary_color: "0b1a3a", name, email, utm_source: appId ? "apply" : "", utm_content: appId || "" })
    .filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return <div data-testid="calendly-embed" className="calendly-inline-widget w-full rounded-2xl border border-mist bg-white" data-url={`${CALENDLY}?${params}`} style={{ minWidth: 320, height: 760 }} />;
}

export default function Book() {
  useMeta({ title: "Book a call with Pep | Smart Marketer Agency", description: "It's a fit. Pick a time for your call with Pep." });
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  const appId = q.get("id");
  const token = q.get("t");
  const [app, setApp] = useState(null);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    if (!appId || !token) return;
    fetch(`${API}/api/applications/${appId}/booking?t=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((a) => { if (a) { setApp(a); if (a.booked_at) setBooked(true); } })
      .catch(() => {});
  }, [appId, token]);

  const onBooked = async (payload) => {
    setBooked(true);
    if (!appId || !token) return;
    try {
      await fetch(`${API}/api/applications/${appId}/booked`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, event_uri: payload.event?.uri || "", invitee_uri: payload.invitee?.uri || "" }),
      });
    } catch {}
  };

  return (
    <main data-testid="draft-book">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">It's a fit</div>
          <h1 className="mt-4 text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Book a call with Pep.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
            {app?.name ? `${app.name.split(" ")[0]}, your` : "Your"} details are in and the numbers line up. Skip the back and forth and pick a time right now.
          </p>
        </div>
      </section>

      <Shell testId="book-body">
        <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr]">
          {booked ? (
            <div data-testid="book-success" className="flex flex-col items-start justify-center rounded-2xl bg-fog p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green text-white"><Check size={26} strokeWidth={3} /></span>
              <h2 className="mt-6 text-2xl font-bold text-navy">Call booked. See you soon.</h2>
              <p className="mt-3 text-base text-navy/70">The invite is on its way to your inbox. Have your ad accounts and last 90 days of numbers handy, and we will make the most of the time.</p>
              <Link to={draftPath("/")} data-testid="book-home-link" className="mt-6 font-bold text-link hover:underline">Back to the site</Link>
            </div>
          ) : (
            <CalendlyInline name={app?.name} email={app?.email} appId={appId} onBooked={onBooked} />
          )}

          <aside data-testid="book-aside">
            <h2 className="text-2xl font-bold text-navy md:text-3xl">The discovery call.</h2>
            <ul className="mt-8 space-y-6">
              {[
                [Clock, "45 minutes", "Where you are, what the main issues are, and whether a 90-day plan makes sense."],
                [Video, "With Pep", "Not a sales rep. You talk to the person who will own the plan."],
                [FileText, "What to bring", "Access to your numbers: revenue, spend, margin, and what the last 90 days looked like."],
              ].map(([Icon, t, b]) => (
                <li key={t} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-white"><Icon size={18} /></span>
                  <div>
                    <div className="font-bold text-navy">{t}</div>
                    <div className="mt-1 text-sm leading-snug text-navy/70">{b}</div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-10 text-sm text-navy/60">No time that works? Email <a href={`mailto:${stats.email}`} className="font-bold text-link">{stats.email}</a> and we will find one.</p>
          </aside>
        </div>
      </Shell>
    </main>
  );
}
